import {Router} from 'vue-router';
import Vue from 'vue';
import Vuex from 'vuex';

declare global { interface Window { digitalData: any; } }

function parseMoney(v: any): number | null {
    if (v == null) return null;
    const cleaned = String(v).replace(/[^0-9,\.\-]/g, '').replace(',', '.');
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
}
function parseDateSmart(s: any): Date | null {
    if (!s) return null;
    const str = String(s).trim();
    const d = new Date(str);
    if (!isNaN(d as any)) return d;
    const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) {
        const d1 = new Date(+m[3], +m[2] - 1, +m[1]);
        if (!isNaN(d1 as any)) return d1;
        const d2 = new Date(+m[3], +m[1] - 1, +m[2]);
        if (!isNaN(d2 as any)) return d2;
    }
    return null;
}
function toISODateStr(s: any): string | null {
    const d = parseDateSmart(s);
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function dateDiffInDays(a: any, b: any): number | null {
    const d1 = parseDateSmart(a), d2 = parseDateSmart(b);
    if (!d1 || !d2) return null;
    d1.setHours(12, 0, 0, 0); d2.setHours(12, 0, 0, 0);
    const ms = (d2 as any) - (d1 as any);
    return ms >= 0 ? Math.round(ms / 86400000) : null;
}
function insuredProfileByCount(qty: number): string {
    if (!qty || isNaN(qty as any) || qty <= 1) return 'Single';
    if (qty === 2) return 'Couple';
    if (qty >= 3 && qty <= 9) return 'Family';
    return 'Big Group';
}
function ageBucketFromAge(age: any): string | null {
    const a = parseInt(age, 10);
    if (isNaN(a)) return null;
    if (a < 20) return '<20';
    if (a <= 29) return '20-29';
    if (a <= 39) return '30-39';
    if (a <= 49) return '40-49';
    if (a <= 54) return '50-54';
    if (a <= 59) return '55-59';
    if (a <= 64) return '60-64';
    if (a <= 79) return '65-79';
    return '80+';
}
function ageFromDob(dob: string): number | null {
    if (!dob) return null;
    const d = parseDateSmart(dob);
    if (!d) return null;
    const diff = Date.now() - (d as any);
    const a = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
    return isNaN(a) ? null : a;
}
function costToBucket(n: any): string {
    const v = parseMoney(n);
    if (v == null) return 'unknown';
    if (v <= 0) return '0';
    if (v <= 490) return '1-490';
    if (v <= 999) return '500-999';
    if (v <= 1499) return '1000-1499';
    if (v <= 1999) return '1500-1999';
    if (v <= 2999) return '2000-2999';
    if (v <= 4999) return '3000-4999';
    return '5000+';
}
function firstMoneyFrom(selectors: string[]): number | null {
    for (const s of selectors) {
        const el = document.querySelector(s) as HTMLElement;
        if (el) {
            const v = parseMoney(el.innerText || el.textContent || '');
            if (v != null) return v;
        }
    }
    const strongs = Array.from(document.querySelectorAll('strong, b'));
    for (const st of strongs) {
        const v = parseMoney((st as HTMLElement).innerText || (st as HTMLElement).textContent || '');
        if (v != null) return v;
    }
    return null;
}

function saveQuote(q: any) { try { sessionStorage.setItem('digitalData.quote', JSON.stringify(q)); } catch (e) { } }
function loadQuote(): any { try { return JSON.parse(sessionStorage.getItem('digitalData.quote') || 'null'); } catch (e) { return null; } }

export function initDigitalData(router: Router, store: any, siteGetter?: () => string) {
    window.digitalData = window.digitalData || {};
    const dd = window.digitalData;

    function pushPage(to: any) {
        const pageName = (to && (to.name || to.path) || 'unknown').toString().replace(/\s+/g, '');
        const siteSection = (
            !to || !to.name ? 'Home' :
                /Payment/i.test(to.name) ? 'Payment' :
                    /Confirm/i.test(to.name) ? 'Confirmation' :
                        /Quote/i.test(to.name) ? 'Quote' :
                            'Sales'
        );
        const partnerId = (siteGetter ? siteGetter() : (store && store.state && (store.state.site || store.state.partnerId))) || '';
        dd.page = dd.page || {};
        dd.page.pageInfo = {
            url: window.location.href,
            language: document.documentElement.lang || 'en',
            pageName,
            siteSection,
        };
        dd.page.partnerId = partnerId;
    }

    router.afterEach((to) => {
        pushPage(to);
    });

    function pushQuoteFromState() {
        const s = store.state || {};
        let age = s.oldestTraveller || null;
        if (!age) {
            const dobs: string[] = [];
            for (let i = 1; i <= 10; i++) {
                const key = `traveller${i}_dob`;
                if (s[key]) dobs.push(s[key]);
            }
            const ages = dobs.map(ageFromDob).filter(v => v != null) as number[];
            if (ages.length) age = Math.max.apply(null, ages);
        }
        const ageBucket = ageBucketFromAge(age);
        const startISO = toISODateStr(s.departureDate);
        const endISO = toISODateStr(s.returnDate);
        const duration = dateDiffInDays(s.departureDate, s.returnDate);
        const num = parseInt(String(s.numberOfTravellers || '').trim() || '0', 10) || 0;
        const profile = insuredProfileByCount(num || 1);
        const province = s.province || null;
        const insuredCost = (s.tripCost != null && String(s.tripCost).trim() !== '' ? costToBucket(s.tripCost) : null);

        const q = {
            quoteDetails: { insuredCost },
            coverage: {
                startDate: startISO || null,
                endDate: endISO || null,
                duration: duration,
                persons: {
                    numberOfPersons: num || 1,
                    insuredProfile: profile,
                    age: ageBucket || null
                }
            },
            policyholder: {
                residenceProvince: province
            }
        };
        dd.quote = q;
        saveQuote(q);
    }

    store.watch(
        (state: any) => ({
            numberOfTravellers: state.numberOfTravellers,
            oldestTraveller: state.oldestTraveller,
            traveller1_dob: state.traveller1_dob,
            traveller2_dob: state.traveller2_dob,
            departureDate: state.departureDate,
            returnDate: state.returnDate,
            tripCost: state.tripCost,
            province: state.province
        }),
        () => pushQuoteFromState(),
        { deep: false, immediate: true }
    );

    router.afterEach((to) => {
        if (!to || !/Confirm/i.test(String(to.name || ''))) return;
        const s = store.state || {};
        const saved = loadQuote() || dd.quote || {};

        const totalPremium = firstMoneyFrom([
            '.total-premium, .summary-total, .premium-total, .displayinfo .label_title li strong, .displayinfo .label_content li strong'
        ]);

        let paymentMethod = '';
        if (s.creditcard) paymentMethod = 'Credit Card';
        if (!paymentMethod) {
            const nodes = Array.from(document.querySelectorAll('.displayinfo .label_content li, .displayinfo .label_title li'));
            const found = nodes.find(el => (el.textContent || '').toLowerCase().includes('payment') || /(credit|debit|paypal|interac|bank)/i.test(el.textContent || ''));
            if (found) {
                const t = (found.textContent || '').toLowerCase();
                if (t.includes('credit')) paymentMethod = 'Credit Card';
                else if (t.includes('debit')) paymentMethod = 'Debit Card';
                else if (t.includes('paypal')) paymentMethod = 'PayPal';
                else if (/(interac|e-?transfer)/.test(t)) paymentMethod = 'Interac e-Transfer';
                else if (t.includes('bank')) paymentMethod = 'Bank Transfer';
            }
        }

        const policyNumber = s.policyNumber || "";
        const postalCode = s.postalcode || s.postalCode || "";

        dd.transaction = {
            currency: 'CAD',
            purchaseId: policyNumber || null,
            totalPrice: totalPremium,
            totalQuantity: 1,
            postalCode: postalCode || null,
            paymentMethod: paymentMethod || null
        };

        dd.product = { name: (s.product && s.product.Product && s.product.Product.ProductName) || null };

        dd.quote = dd.quote || {};
        dd.quote.quoteDetails = Object.assign({}, dd.quote.quoteDetails, {
            insuredCost: saved && saved.quoteDetails ? saved.quoteDetails.insuredCost : null
        });
        dd.quote.coverage = Object.assign({}, dd.quote.coverage, {
            startDate: (saved && saved.coverage && saved.coverage.startDate) || null,
            endDate: (saved && saved.coverage && saved.coverage.endDate) || null
        });
    });

    pushPage(router.currentRoute);
}